/** Small pieces every screen shows the same way: stage pills, phones, empty values. */
import * as React from "react";
import { Link } from "react-router";
import type { Email, HouseStatus, LeadSummary, Phone, SourceTag, Stage } from "@/api/models";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { HOUSE_STATUS_LABELS, SOURCE_TAG_LABELS, STAGE_LABELS, STAGE_TONES } from "@/lib/labels";
import { prettyDay } from "@/lib/dates";
import { courtListedLabels, courtListedWords, formatPhone, isEstateAddressee, isSamplePhone, leadHref, leadPersonName, telHref } from "@/lib/lead";
import { shortDay } from "@/lib/next-step";
import { cn } from "@/lib/utils";

const TONE_VARIANT = { ok: "success", wait: "neutral", warn: "warning", muted: "outline" } as const;

export function StagePill({ stage, label, dropped, className }: { stage: Stage; label?: string; dropped?: boolean; className?: string }) {
  if (dropped) {
    return (
      <Badge variant="outline" dot className={className} data-testid="stage-pill">
        Dropped
      </Badge>
    );
  }
  return (
    <Badge variant={TONE_VARIANT[STAGE_TONES[stage]]} dot className={className} data-testid="stage-pill">
      {label ?? STAGE_LABELS[stage]}
    </Badge>
  );
}

/**
 * KORI status trio: green still in the deceased's name, amber listed, red sold, muted unknown.
 * A house to ask about on the call (a similar name, the family name or a trust on the roll)
 * reads in info blue.
 */
const HOUSE_VARIANT = {
  available: "success",
  public_record: "success",
  lived_here: "info",
  listed: "warning",
  sold: "danger",
  similar_name: "info",
  name_unconfirmed: "info",
  family_name: "info",
  trust: "info",
  unknown: "outline",
} as const;

/** What the tax roll says when it does not show the deceased still owning the house. */
const HOUSE_QUESTION: Partial<Record<HouseStatus, string>> = {
  public_record:
    "Dated public records corroborate that the deceased owned this property. Current title and sale authority remain unknown.",
  similar_name:
    "The tax roll names someone with the deceased's first and last name but a different middle name. It may be a relative with the same name, so ask on the call.",
  name_unconfirmed:
    "The tax roll names someone with the deceased's name, but nothing yet shows it is the deceased: the name is common, or the house is not where the court says they lived. Ask on the call whether it was theirs.",
  family_name:
    "At the address the court gives for the deceased, the tax roll names someone who shares the family name, most likely a spouse or relative. Ask on the call who owns the house now.",
  trust: "The tax roll names a trust as the owner. It may be the family's trust, so ask on the call who manages it.",
  lived_here:
    "The court gives this house as the deceased's home. The state does not publish owner names, so ask on the call whether the family owns it.",
  unknown: "Not known from the tax roll.",
};

/**
 * The house badge. It never outgrows its cell: in a narrow column the words wrap onto a
 * second line, and the tooltip always carries the whole label, with the day the tax roll
 * row was read when the API has it.
 */
export function HouseBadge({ status, rollDate, className }: { status: HouseStatus; rollDate?: string | null; className?: string }) {
  const label = HOUSE_STATUS_LABELS[status];
  const detail =
    HOUSE_QUESTION[status] ?? (rollDate ? `${label} on the tax roll dated ${prettyDay(rollDate)}.` : `${label}.`);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={HOUSE_VARIANT[status]}
          size="sm"
          dot
          tabIndex={0}
          className={cn(
            "max-w-full shrink items-start whitespace-normal rounded-[0.75rem] text-left [&>span:first-child]:mt-[0.42em]",
            className,
          )}
          data-testid="house-status"
          data-status={status}
        >
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}

const SOURCE_VARIANT: Record<SourceTag, "neutral" | "info" | "success" | "sample" | "outline"> = {
  court: "neutral",
  tax_roll: "info",
  document: "success",
  sample: "sample",
  skip_trace: "neutral",
  state_bar: "neutral",
  firm_website: "neutral",
  none: "outline",
};

/** Where a value came from, as a small tag: Court, Tax roll, Document, State Bar, Firm website. */
export function SourceBadge({ tag, className }: { tag: SourceTag; className?: string }) {
  return (
    <Badge size="sm" variant={SOURCE_VARIANT[tag]} className={className} data-testid="source-tag" data-source={tag}>
      {SOURCE_TAG_LABELS[tag]}
    </Badge>
  );
}

export function DueBadge() {
  return (
    <Badge variant="warning" size="sm">
      Due
    </Badge>
  );
}

const SAMPLE_PHONE_NOTE = "A placeholder, not a real number. It is never dialled or exported.";
const SAMPLE_EMAIL_NOTE = "A placeholder, not a real address. It is never written to or exported.";
const SAMPLE_CONTACT_NOTE = "Placeholders, not real contacts. They are never dialled, written to or exported.";

export function SampleBadge({ note = SAMPLE_PHONE_NOTE }: { note?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="sample" size="sm" tabIndex={0} data-testid="sample-badge">
          Sample
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{note}</TooltipContent>
    </Tooltip>
  );
}

export function ExampleBadge() {
  return (
    <Badge variant="outline" size="sm">
      Example
    </Badge>
  );
}

export function NotFound({ children }: { children: React.ReactNode }) {
  return <span className="text-muted italic">{children}</span>;
}

/** One phone: a tel: link when real and dialable; a sample stays plain text with its badge. */
export function PhoneValue({ phone, badge = true }: { phone: Phone; badge?: boolean }) {
  if (!phone.value) return null;
  const href = telHref(phone);
  if (!href) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid="phone" data-sample={isSamplePhone(phone)}>
        <span className="font-mono text-muted">{isSamplePhone(phone) ? phone.value : formatPhone(phone.value)}</span>
        {badge && isSamplePhone(phone) ? <SampleBadge /> : null}
      </span>
    );
  }
  return (
    <a className="whitespace-nowrap font-mono text-link underline-offset-2 hover:underline" href={href} data-testid="phone" data-sample="false">
      {formatPhone(phone.value)}
    </a>
  );
}

/** The values a table row leaves out, behind "+N more", whose tooltip lists them. */
function MoreBadge({ labels, testId }: { labels: string[]; testId: string }) {
  if (!labels.length) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" size="sm" tabIndex={0} data-testid={testId}>
          +{labels.length} more
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        <span className="flex flex-col gap-0.5 font-mono">
          {labels.map((label) => (
            <span key={label}>{label}</span>
          ))}
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

function capped<T>(items: T[], limit?: number): [T[], T[]] {
  const visible = limit && items.length > limit ? items.slice(0, limit) : items;
  return [visible, items.slice(visible.length)];
}

const phoneLabel = (phone: Phone) => (isSamplePhone(phone) ? `${phone.value ?? ""} (sample)` : formatPhone(phone.value ?? ""));
const emailLabel = (email: Email) => (email.source === "sample" ? `${email.value} (sample)` : email.value);

/**
 * The petitioner's phones, one per line. `limit` caps how many a table row shows; the rest
 * sit behind "+N more", whose tooltip lists them.
 */
export function PhoneList({
  phones,
  empty = "None",
  className,
  limit,
  badges = true,
}: {
  phones: Phone[];
  empty?: string;
  className?: string;
  limit?: number;
  badges?: boolean;
}) {
  const shown = phones.filter((phone) => phone.value);
  if (!shown.length) return <NotFound>{empty}</NotFound>;
  const [visible, hidden] = capped(shown, limit);
  return (
    <span className={cn("inline-flex flex-col items-end gap-1.5 md:items-start", className)}>
      {visible.map((phone) => (
        <PhoneValue key={phone.value} phone={phone} badge={badges} />
      ))}
      <MoreBadge labels={hidden.map(phoneLabel)} testId="more-phones" />
    </span>
  );
}

/** An address that may wrap only before the @ and after a dot, never mid-word. */
function EmailText({ value }: { value: string }) {
  const at = value.lastIndexOf("@");
  const parts = at > 0 ? [value.slice(0, at), value.slice(at)] : [value];
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index ? <wbr /> : null}
          {part.split(/(?<=\.)/).map((piece, i) => (
            <React.Fragment key={i}>
              {i ? <wbr /> : null}
              {piece}
            </React.Fragment>
          ))}
        </React.Fragment>
      ))}
    </>
  );
}

/** One email: a mailto: link when real; a sample stays plain text with its badge. */
export function EmailValue({ email, badge = true }: { email: Email; badge?: boolean }) {
  if (email.source === "sample") {
    return (
      <span className="inline-flex max-w-full flex-wrap items-center gap-2" data-testid="email" data-sample="true">
        <span className="font-mono text-xs text-muted [overflow-wrap:anywhere]">
          <EmailText value={email.value} />
        </span>
        {badge ? <SampleBadge note={SAMPLE_EMAIL_NOTE} /> : null}
      </span>
    );
  }
  return (
    <a
      className="max-w-full font-mono text-xs text-link underline-offset-2 [overflow-wrap:anywhere] hover:underline"
      href={`mailto:${email.value}`}
      data-testid="email"
      data-sample="false"
    >
      <EmailText value={email.value} />
    </a>
  );
}

/**
 * How to reach the petitioner: every phone, then every email. `limit` caps how many of each
 * a table row shows; the rest sit behind "+N more".
 */
export function ContactList({ phones, emails, empty = "None", limit }: { phones: Phone[]; emails: Email[]; empty?: string; limit?: number }) {
  const shownPhones = phones.filter((phone) => phone.value);
  if (!shownPhones.length && !emails.length) return <NotFound>{empty}</NotFound>;
  const [visible, hidden] = capped(emails, limit);
  // When every contact is a placeholder, one badge says so for the lot.
  const allSample = shownPhones.every(isSamplePhone) && emails.every((email) => email.source === "sample");
  return (
    <span className="flex min-w-0 flex-col items-end gap-1.5 md:items-start" data-testid="contact">
      {shownPhones.length ? <PhoneList phones={shownPhones} limit={limit} badges={!allSample} /> : null}
      {visible.map((email) => (
        <EmailValue key={email.value} email={email} badge={!allSample} />
      ))}
      <MoreBadge labels={hidden.map(emailLabel)} testId="more-emails" />
      {allSample ? <SampleBadge note={SAMPLE_CONTACT_NOTE} /> : null}
    </span>
  );
}

/** The petitioner's name, linking to the lead and remembering where the agent came from. */
export function LeadLink({
  lead,
  from,
}: {
  lead: Pick<LeadSummary, "lead_id" | "petitioner_name" | "reported_representatives" | "court_listed_people" | "deceased_name" | "is_example" | "source_attributions">;
  from: string;
}) {
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <Link to={leadHref(lead.lead_id)} state={{ from }} className="font-semibold text-link hover:underline">
        {leadPersonName(lead) || "Petitioner not yet identified"}
      </Link>
      {lead.reported_representatives.length ? (
        <span className="text-xs font-normal text-muted">
          {lead.petitioner_name && !isEstateAddressee(lead.petitioner_name)
            ? `Reported representative: ${lead.reported_representatives.map((person) => person.name).join("; ")}`
            : "Reported representative"}
          {lead.reported_representatives.some((person) => person.review_note) ? " · Needs review" : ""}
        </span>
      ) : null}
      {!leadPersonName(lead) && lead.court_listed_people.length ? (
        <span className="text-xs font-normal text-muted" data-testid="court-listed" title={courtListedLabels(lead.court_listed_people)}>
          {courtListedWords(lead.court_listed_people)}
        </span>
      ) : null}
      <SourceAttributions items={lead.source_attributions} />
      {lead.is_example ? <ExampleBadge /> : null}
    </span>
  );
}

/** The case's date in a list: the filing day, or what the date is when it is not one. */
export function FiledCell({ lead, today }: { lead: Pick<LeadSummary, "filing_date" | "filing_date_label">; today: string }) {
  return (
    <span className="flex flex-col whitespace-nowrap">
      <span>{shortDay(lead.filing_date, today)}</span>
      {lead.filing_date_label !== "Filed" ? <span className="text-xs text-muted">{lead.filing_date_label.toLowerCase()}</span> : null}
    </span>
  );
}

/** Original copy dates remain separate from filing and processing dates. */
export function SourceAttributions({ items, withNotice = false }: { items: NonNullable<LeadSummary["source_attributions"]>; withNotice?: boolean }) {
  if (!items.length) return null;
  return <span className="flex flex-col gap-1 text-xs font-normal text-muted" data-testid="source-attributions">
    {items.map((item) => <span key={`${item.url}:${item.copied_at}`}>
      <a href={item.url} target="_blank" rel="noreferrer" className="text-link underline-offset-2 hover:underline" title={item.source}>{withNotice ? item.url : "Source"}</a>
      {" · Copied "}{item.copied_at.slice(0, 10)}
      {withNotice ? <span className="mt-1 block">{item.notice}</span> : null}
    </span>)}
  </span>;
}
